import React, { useState } from "react";
import { adminMemoryService } from "../../services/admin-memory.service";
import { useToast } from "../../context/ToastContext";
import type { Memory, UpdateMemoryDto } from "../../services/memory.service";
import "../../styles/clients.css";

interface Props {
    entry: Memory;
    onClose: () => void;
    onUpdated: () => void;
}

export default function EditAdminKnowledgeModal({ entry, onClose, onUpdated }: Props) {
    const { showToast } = useToast();
    const [question, setQuestion] = useState(entry.question);
    const [answer, setAnswer] = useState(entry.answer);
    const [category, setCategory] = useState(entry.metadata?.category || "");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const payload: UpdateMemoryDto = {};
            if (question.trim() !== entry.question) payload.question = question.trim();
            if (answer.trim() !== entry.answer) payload.answer = answer.trim();
            if (category !== (entry.metadata?.category || "")) payload.category = category || undefined;

            if (Object.keys(payload).length === 0) { onClose(); return; }

            await adminMemoryService.update(entry.id, payload);
            showToast("Knowledge entry updated successfully.");
            onUpdated();
        } catch (err: any) {
            const msg = err.response?.data?.message || err.response?.data?.msg || "Failed to update entry.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <h2>Edit Knowledge</h2>
                <p className="modal-subtitle">Update this Q&A entry</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-field">
                        <label htmlFor="edit-admin-kb-question">Question</label>
                        <textarea
                            id="edit-admin-kb-question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-admin-kb-answer">Answer</label>
                        <textarea
                            id="edit-admin-kb-answer"
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-admin-kb-category">Category</label>
                        <input
                            id="edit-admin-kb-category"
                            type="text"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            placeholder="e.g. faq, product, policy"
                        />
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-modal-submit" disabled={loading}>
                            {loading ? "Saving..." : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
